import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import DataRoomUploadContext from 'router/subrouters/DataRoom/providers/DataRoomUpload/DataRoomUpload.context';
import DataRoomDocumentsContext from '../../../providers/DataRoomDocuments/DataRoomDocuments.context';
import { getIsArrayEmpty, getIsNodeFile, getIsNodeFolder } from 'utils';
import { MAX_PERCENTAGE } from 'data/global';

export default function useUploadingPlaceholders(documentId: string) {
  const { currentlyUploading } = useContext(DataRoomUploadContext);
  const { currentlyDisplayedNodes } = useContext(DataRoomDocumentsContext);

  const stillUploading = useMemo(
    () =>
      currentlyUploading.filter(
        ({ isFailed, parentId }) => !isFailed && parentId === documentId,
      ),
    [currentlyUploading, documentId],
  );

  const stillUploadingByType = useMemo(() => {
    const folders: Record<string, typeof stillUploading> = {};
    const files: typeof stillUploading = [];

    stillUploading.forEach((item) => {
      if (item.folderName) {
        if (!folders[item.folderName]) {
          folders[item.folderName] = [];
        }
        folders[item.folderName].push(item);
      } else {
        files.push(item);
      }
    });

    return { folders, files };
  }, [stillUploading]);

  const acknowledgedUploadsRef = useRef<Set<string>>(new Set());
  const hasPreAcknowledgedRef = useRef(false);

  const acknowledgeUpload = useCallback(
    (key: string) => acknowledgedUploadsRef.current.add(key),
    [],
  );

  useEffect(() => {
    hasPreAcknowledgedRef.current = false;
  }, [documentId]);

  useEffect(() => {
    if (hasPreAcknowledgedRef.current) return;
    hasPreAcknowledgedRef.current = true;

    stillUploading.forEach(
      ({ name, folderName, percentage, replacingFileName }) => {
        if (percentage !== MAX_PERCENTAGE) return;

        acknowledgeUpload(
          folderName
            ? `folder:${folderName}`
            : `file:${replacingFileName || name}`,
        );
      },
    );
  }, [documentId, stillUploading, acknowledgeUpload]);

  useEffect(() => {
    if (getIsArrayEmpty(stillUploading)) {
      acknowledgedUploadsRef.current.clear();
      return;
    }

    const renderedFolderNames = new Set(
      currentlyDisplayedNodes.filter(getIsNodeFolder).map(({ name }) => name),
    );
    const renderedFileNames = new Set(
      currentlyDisplayedNodes.filter(getIsNodeFile).map(({ name }) => name),
    );

    Object.keys(stillUploadingByType.folders).forEach((folderName) => {
      if (renderedFolderNames.has(folderName)) {
        acknowledgeUpload(`folder:${folderName}`);
      }
    });

    stillUploadingByType.files.forEach(({ name, replacingFileName }) => {
      const key = replacingFileName || name;
      if (renderedFileNames.has(key)) {
        acknowledgeUpload(`file:${key}`);
      }
    });
  }, [
    currentlyDisplayedNodes,
    stillUploading,
    stillUploadingByType,
    acknowledgeUpload,
  ]);

  const placeholdersForRender = useMemo(() => {
    const renderedFolderNodes = currentlyDisplayedNodes.filter(getIsNodeFolder);
    const renderedFileNodes = currentlyDisplayedNodes.filter(getIsNodeFile);

    return {
      folders: Object.keys(stillUploadingByType.folders).filter(
        (folderName) =>
          renderedFolderNodes.every(({ name }) => name !== folderName) &&
          !acknowledgedUploadsRef.current.has(`folder:${folderName}`),
      ),
      files: stillUploadingByType.files.filter(
        ({ name: fileName, replacingFileName }) =>
          renderedFileNodes.every(
            ({ name }) => ![fileName, replacingFileName].includes(name),
          ) &&
          !acknowledgedUploadsRef.current.has(`file:${fileName}`) &&
          !(
            replacingFileName &&
            acknowledgedUploadsRef.current.has(`file:${replacingFileName}`)
          ),
      ),
    };
  }, [
    currentlyDisplayedNodes,
    stillUploadingByType.files,
    stillUploadingByType.folders,
  ]);

  const getIsStillUploadingNode = useCallback(
    (node) => {
      const isFolder = getIsNodeFolder(node);

      if (isFolder)
        return Object.entries(stillUploadingByType.folders).some(
          ([folderName, files]) =>
            node.name === folderName &&
            files.some(({ percentage }) => percentage !== MAX_PERCENTAGE),
        );

      return stillUploadingByType.files.some(
        ({ name: fileName, replacingFileName, percentage }) =>
          (replacingFileName
            ? replacingFileName === node.name
            : fileName === node.name) && percentage !== MAX_PERCENTAGE,
      );
    },
    [stillUploadingByType],
  );

  const displayedNodeIdsUploading = useMemo(
    () =>
      currentlyDisplayedNodes
        .filter(getIsStillUploadingNode)
        .map(({ _id }) => _id),
    [currentlyDisplayedNodes, getIsStillUploadingNode],
  );

  return { placeholders: placeholdersForRender, displayedNodeIdsUploading };
}
